import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { SignModal } from '../ConnectPage/components/SignModal/SignModal';
import { ConnectorButton } from '../ConnectPage/components/connect-section/connector-button';
import * as Sentry from '@sentry/react';
import bs58 from 'bs58';

import { defaultWallets, walletsUrl } from 'modules/ConnectPage/components/connect-section/config';

import { Header } from 'shared/components/sections/header/header';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { CountdownTimer } from 'shared/components/ui/CountdownTimer/CountdownTimer';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { ROUTES } from 'shared/constants';
import { useAccount, useConnect } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { useShowSignModal } from 'shared/hooks/useShowSignModal';
import { useGlobalStore } from 'shared/store/globalStore';

export const Verify = () => {
  const { connectors, connect } = useConnect();
  const { t } = useTranslation();
  const { isConnected, address } = useAccount();
  const hasConnectors = connectors && connectors.length > 0;
  const availableWallets = hasConnectors ? connectors : defaultWallets;
  const { showSignModal } = useShowSignModal();
  const { jwt, jwtValidUntil } = useGlobalStore();
  const { discordId } = useParams();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const handleVerify = async () => {
      const maxRetries = 3;
      const baseDelay = 5000;
      let attempt = 0;
      let date = jwtValidUntil[address!];

      if (!date) {
        return;
      }

      let message = `address: ${address} \nvalid until: ${new Date(date).toISOString()}`;

      // Using Base58 encoding instead of URL encoding
      const messageBytes = new TextEncoder().encode(message);
      const base58Message = bs58.encode(messageBytes);

      while (attempt <= maxRetries) {
        try {
          let date = jwtValidUntil[address!];

          if (!date) {
            return { ok: false };
          }

          const response = await fetch('/api/verify', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              verify: discordId!,
              signature: jwt[address!],
              message: base58Message,
            }),
          });

          if (response?.ok && response.status === 200) {
            const currentDate = new Date();
            const futureDate = new Date(currentDate.getTime() + 10000);
            setDate(futureDate);
            const responseData = await response.json();
            if (responseData.message) {
              setMessage(responseData.message);
            }
            setTimeout(() => {
              navigate(ROUTES.LOBBY);
            }, 10000);
            return { ok: true };
          } else if (!response?.ok) {
            const responseData = await response.json();
            if (responseData.error) {
              setMessage(responseData.error);
            } else if (responseData.message) {
              setMessage(responseData.message);
            }

            if (response.status >= 500) {
              console.error(`Attempt ${attempt + 1}: Server returned status ${response.status}`);
            } else {
              setError(true);
              console.error(
                `Attempt ${attempt + 1}: Server returned status ${response.status}. No retry will be attempted.`,
              );
              break;
            }
          }
        } catch (error) {
          console.error(`Attempt ${attempt + 1}: Error verifying -`, error);
        }

        attempt++;
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt - 1);
          console.log(`Retrying in ${delay} ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          console.error('Max retries reached. Unable to verify.');
        }
      }
      return { ok: false };
    };
    if (!showSignModal && jwt[address!] && address && jwtValidUntil[address!]) {
      handleVerify();
    }
  }, [showSignModal, jwt, address, jwtValidUntil]);
  const isMobile = useMediaQuery('(max-width: 448px)');
  return (
    <>
      <AnimatedTransition show={showSignModal}>
        <SignModal />
      </AnimatedTransition>
      <div className="relative h-full w-full overflow-hidden">
        <div className="flex h-screen flex-col items-center overflow-hidden">
          <div className="z-50 mx-auto mb-4 w-full max-w-[1920px] px-8 pt-[32px] 2xl:mb-12 xlg:px-[64px]">
            <Header title="Verify" />
          </div>
          <AnimatedTransition show={!isConnected} asFragment>
            <div className="mb-5 flex w-full max-w-[754px] flex-col items-center rounded-[16px] border border-border bg-one-more-blue px-3 pb-3 pt-4">
              <p className="gradient-title mb-[12px] text-[40px] font-bold capitalize">
                {t('common.connect')} <span>{t('common.aWallet')}</span>
              </p>
              {isMobile ? (
                <div
                  className={`grid w-full gap-1.5 ${
                    availableWallets.length % 3 === 0
                      ? 'grid-cols-3'
                      : availableWallets.length % 2 === 0
                        ? 'grid-cols-2'
                        : 'grid-cols-1'
                  }`}
                >
                  {availableWallets.length === 1 && (
                    <PrimaryButton
                      onClick={() => {
                        Sentry.setTag('wallet', availableWallets[0].name);
                        connect();
                      }}
                      svg={
                        <img
                          src={availableWallets[0].icon}
                          alt={availableWallets[0].name}
                          className="mr-3 h-8 w-8 sm:h-[56px] sm:w-[56px]"
                        />
                      }
                      text={`${t('common.connect')} ${availableWallets[0].name}`}
                    />
                  )}
                  {availableWallets.length > 1 &&
                    availableWallets.map((connector) => (
                      <ConnectorButton
                        key={connector.name}
                        connector={connector}
                        onClick={() => (hasConnectors ? connect() : connector.connect())}
                      />
                    ))}
                </div>
              ) : (
                <>
                  {' '}
                  <div
                    className={`grid w-full gap-1.5 ${
                      availableWallets.length % 3 === 0
                        ? 'grid-cols-3'
                        : availableWallets.length % 2 === 0
                          ? 'grid-cols-2'
                          : 'grid-cols-1'
                    }`}
                  >
                    {availableWallets.length > 1 &&
                      availableWallets.map((connector) => (
                        <ConnectorButton
                          key={connector.name}
                          connector={connector}
                          onClick={() => {
                            hasConnectors
                              ? connect()
                              : window.open(walletsUrl[connector.id], '_blank');
                          }}
                        />
                      ))}
                  </div>
                  {availableWallets.length === 1 && (
                    <PrimaryButton
                      text={'common.connect'}
                      onClick={() => {
                        Sentry.setTag('wallet', availableWallets[0].name);
                        connect();
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </AnimatedTransition>
          <AnimatedTransition show={!!(isConnected && date)} asFragment>
            <div className="flex flex-col items-center gap-4">
              {' '}
              <p className="font-bold text-white md:text-2xl">{message}</p>
              <CountdownTimer expiryTimestamp={date!} />
            </div>
          </AnimatedTransition>
          <AnimatedTransition show={error} asFragment>
            <div className="flex flex-col items-center gap-4">
              {message && <p className="font-bold text-white md:text-2xl">{message}</p>}
              <p className="font-bold text-white md:text-2xl">Something went wrong</p>
              <p className="font-bold text-white md:text-2xl">Ask for help in Discord </p>
            </div>
          </AnimatedTransition>
        </div>
      </div>
    </>
  );
};
